package com.siriusxm.pia.views.unifiedaggregator.backfill

import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import androidx.compose.runtime.*
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.NotificationTypes
import com.siriusxm.cmc.error
import com.siriusxm.cmc.utils.toShortDateTimeString
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.*
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.withTimeout
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import kotlin.time.Instant
import smithy4kt.SmithyClientError
import kotlin.time.Duration.Companion.seconds

/**
 * Shows a dialog to configure and launch a backfill job. The target ARN and role are typically
 * pre-populated from the consumer targets page via query parameters.
 */
@Composable
fun startBackfill(
    aggregator: AggregatorService, copyTarget: String? = null,
    copyRole: String? = null,
    copyTypes: Collection<EntityTypeConfiguration>? = null
) {
    var success by remember { mutableStateOf<Boolean?>(null) }
    var errors by remember { mutableStateOf<List<String>?>(null) }

    var role by remember { mutableStateOf(copyRole) }
    var targetArn by remember { mutableStateOf(copyTarget) }
    var types by remember { mutableStateOf(copyTypes ?: emptyList()) }
    var startTimestamp by remember { mutableStateOf<Instant?>(null) }
    var endTimestamp by remember { mutableStateOf<Instant?>(null) }

    var pitrTimestamp by remember { mutableStateOf<Instant?>(null) }
    var pitrLoaded by remember { mutableStateOf(false) }
    var pitrError by remember { mutableStateOf(false) }

    var showAdvancedFeatures = remember { mutableStateOf(false) }
    var includeSourceIds by remember { mutableStateOf(false) }
    var includeRemovedEntities by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        try {
            val status = aggregator.clientApi.getBackfillStatus()
            pitrTimestamp = status.lastPointInTimeTs
        } catch (e: SmithyClientError) {
            if (e.httpStatusCode == 404) {
                pitrError = true
            }
        } catch (_: Throwable) {
            // Informational only — don't block the user
        } finally {
            pitrLoaded = true
        }
    }

    fun resetAdvancedDefaults() {
        startTimestamp = null
        endTimestamp = null
        includeSourceIds = false
        includeRemovedEntities = false
    }

    /**
     * Run the test
     */
    suspend fun testBackfill(role: String?, targetArn: String) {
        try {
            val testResult = aggregator.clientApi.testBackfill(
                BackfillTestRequest(targetArn, role?.takeIf { it.isNotBlank() })
            )
            success = testResult.success
            errors = if (!testResult.success) {
                listOfNotNull(testResult.message)
            } else {
                emptyList()
            }
        } catch (e: SmithyClientError) {
            if (e.httpStatusCode == 400) {
                errors = listOf(e.message ?: "Unknown error")
            } else {
                val failure = intakeErrorMessage("test this target", e)
                aggregator.notifications.error(failure.message, failure.description)
            }
        } catch (t: Throwable) {
            t.printStackTrace()
            val failure = intakeErrorMessage("test this target", t)
            aggregator.notifications.error(failure.message, failure.description)
        }
    }

    // Execute the backfill.
    suspend fun runBackfill() {
        if (targetArn == null) {
            errors = listOf("You must enter a target ARN")
            return
        }
        if (types.isEmpty()) {
            errors = listOf("You must select at least one type")
            return
        }

        val filterResult = validateBackfillDateRange(startTimestamp, endTimestamp)
        if (filterResult.isFailure) {
            errors = listOf(filterResult.exceptionOrNull()?.message ?: "Invalid date range")
            return
        }

        try {
            val status = aggregator.clientApi.postBackfillRequest(
                BackfillRequest(
                    identifiers = Identifiers.MAPPED,
                    targetArn!!,
                    role?.takeIf { it.isNotBlank() },
                    types.map {
                        it.schema
                    },
                    filter = filterResult.getOrThrow(),
                    includeSourceIds = includeSourceIds,
                    includeRemovedEntities = includeRemovedEntities
                )
            )

            // wait for the job to be 'realized', and then navigate to it.
            try {
                withTimeout(10.seconds) {
                    while (true) {
                        try {
                            aggregator.clientApi.getBackfillJob(status.id)
                            break
                        } catch (_: Throwable) {
                            delay(1.seconds)
                        }
                    }
                    aggregator.navigate("backfill/${status.id}")
                }
            } catch (_: TimeoutCancellationException) {
                aggregator.notifications.error("The backfill job hasn't started yet. It may yet still, we've just timed out waiting for it.")
                aggregator.navigate("backfill")
            }

        } catch (t: Throwable) {
            t.printStackTrace()
            val failure = intakeErrorMessage("start this backfill", t)
            aggregator.notifications.error(failure.message, failure.description)
        }
    }


    dialogView("Run Backfill") {
        content {
            if (!pitrLoaded) {
                spinner(size = Size.LARGE)
                return@content
            }

            if (pitrError) {
                NotificationBox(
                    Notification(
                        NotificationTypes.Error,
                        "Backfill is currently unavailable.",
                        "No point-in-time snapshot exists. A snapshot must be available before a backfill can be started."
                    )
                )
                return@content
            }

            if (pitrTimestamp != null) {
                dialogText(
                    "Data Snapshot",
                    // Render in the globally-selected timezone (header picker).
                    pitrTimestamp!!.toLocalDateTimeIn(currentTimeZoneId()).toShortDateTimeString(),
                    "Backfill data will be sourced from this point-in-time snapshot."
                )
            }

            dialogField("Target ARN", "The target ARN of the backfill (Kinesis Stream, Event Bridge Bus, etc.)") {
                simpleTextField(targetArn) {
                    targetArn = it
                    success = null
                }
            }

            dialogField("Role ARN", "The ARN of the role used to publish data to the target.") {
                simpleTextField(role) {
                    role = it
                    success = null
                }
            }

            dialogField("Types", "Select the types to include in the backfill") {
                box({
                    paddedContent = false
                }) {
                    scrollableCheckBoxSelector(
                        items = aggregator.entityTypes.sortedBy { it.name },
                        selectedItems = types.map { it.type }.toSet(),
                        itemLabel = { it.name },
                        itemIdentifier = { it.type },
                        onSelectionChange = { selectedTypes ->
                            types = aggregator.entityTypes.filter { it.type in selectedTypes }
                        }
                    )
                }
            }
            val showAdvanced = showAdvancedFeatures.value
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                    cursor("pointer")
                    property("user-select", "none")
                    marginBottom(if (showAdvanced) 12.px else 0.px)
                }
                onClick {
                    if (!showAdvanced) {
                        showAdvancedFeatures.value = true
                    } else {
                        resetAdvancedDefaults()
                        showAdvancedFeatures.value = false
                    }
                }
            }) {
                expandingIcon(showAdvanced)
                org.jetbrains.compose.web.dom.Span({
                    style { fontWeight("bold") }
                }) {
                    org.jetbrains.compose.web.dom.Text("Advanced")
                }
            }

            if (showAdvanced) {
                dialogField(
                    "Start Timestamp",
                    "Optional. Leave blank to include all historical changes."
                ) {
                    instantPicker(
                        startTimestamp,
                        limit = endTimestamp?.let { OpenEndedRange(null, it) }
                    ) {
                        startTimestamp = it
                    }
                }
                dialogField(
                    "End Timestamp",
                    "Optional. Leave blank to include changes up to the present."
                ) {
                    instantPicker(
                        endTimestamp,
                        limit = startTimestamp?.let { OpenEndedRange(it, null) }
                    ) {
                        endTimestamp = it
                    }
                }

                Div {
                    simpleCheckBox("Include source ids", includeSourceIds) {
                        includeSourceIds = it
                    }
                }

                if (aggregator.isAdmin()) {
                    Div {
                        simpleCheckBox("Include removed entities", includeRemovedEntities) {
                            includeRemovedEntities = it
                        }
                    }
                }
            }

            if (success == true) {
                NotificationBox(
                    Notification(
                        style = NotificationTypes.Info, message = "Success!",
                        description = "Your backfill target and role are configured correctly!"
                    )
                ) {

                }
            }
            errors?.forEach { error ->
                NotificationBox(
                    Notification(
                        style = NotificationTypes.Error, message = error, description = null
                    )
                ) {}
            }
        }

        if (pitrLoaded) {
            this.action {
                title = "Cancel"
                enabled = true
                action {
                    aggregator.navigate("backfill")
                }
            }

            this.action {
                title = "Start"
                primary = true
                enabled = !targetArn.isNullOrEmpty() && types.isNotEmpty() && !pitrError
                showProgressOnAction = true
                action {
                    errors = null
                    runBackfill()
                }
            }

            this.action {
                title = "Test"
                primary = true
                enabled = !targetArn.isNullOrEmpty()
                showProgressOnAction = true
                action {
                    errors = null
                    testBackfill(role, targetArn ?: "")
                }
            }
        }
    }
}

/**
 * Validate that the provided date range is valid.
 *
 * Constraints:
 * - startTs must not be in the future (must be <= now)
 * - endTs must not be in the future (must be <= now)
 * - startTs must be before endTs if both are provided
 * - Both timestamps are optional (null is allowed)
 *
 * @param startTs Optional start timestamp for the backfill date range
 * @param endTs Optional end timestamp for the backfill date range
 * @return Result containing a BackfillDataFilter on success, or a failure with an IllegalArgumentException on validation error
 */
private fun validateBackfillDateRange(
    startTs: Instant?,
    endTs: Instant?
): Result<BackfillDataFilter> {
    val now = kotlin.time.Clock.System.now()

    if (startTs != null && startTs > now) {
        return Result.failure(IllegalArgumentException("The start timestamp cannot be in the future."))
    }

    if (endTs != null && endTs > now) {
        return Result.failure(IllegalArgumentException("The end timestamp cannot be in the future."))
    }

    if (startTs != null && endTs != null && startTs >= endTs) {
        return Result.failure(IllegalArgumentException("The start timestamp must be before the end timestamp."))
    }

    return Result.success(
        BackfillDataFilter(
            startTs = startTs,
            endTs = endTs
        )
    )
}
